import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import ProcessPayrollModal from '../components/ProcessPayrollModal';
import UpgradePrompt from '../components/UpgradePrompt';
import usePlanFeature from '../hooks/usePlanFeature';
import { toast } from '../components/Toast';
import './PayrollPage.css';

function PayrollPage() {
  const { enabled: payrollEnabled, planTier } = usePlanFeature('payroll');

  const [payrollRecords, setPayrollRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    employeeId: '',
    page: 0,
    size: 20
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchPayrollRecords();
    fetchEmployees();
  }, [filters]);

  const fetchPayrollRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page);
      params.append('size', filters.size);
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      
      const response = await axios.get(`/payroll?${params.toString()}`);
      const data = response.data.data || response.data;
      setPayrollRecords(data.content || []);
      setPagination({
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        currentPage: data.page || 0
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch payroll records');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employees?size=1000');
      const data = response.data.data?.content || response.data.content || [];
      setEmployees(data);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 0
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      employeeId: '',
      page: 0,
      size: 20
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleProcessPayroll = () => {
    setShowProcessModal(true);
  };

  const handleDownloadSalarySlip = async (payrollId, employeeCode) => {
    try {
      const response = await axios.get(`/payroll/${payrollId}/salary-slip`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_slip_${employeeCode}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Salary slip downloaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download salary slip');
    }
  };

  const handleModalClose = () => {
    setShowProcessModal(false);
  };

  const handleModalSuccess = () => {
    setShowProcessModal(false);
    toast.success('Payroll processed successfully');
    fetchPayrollRecords();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      PENDING: 'badge-pending',
      PROCESSED: 'badge-processed',
      PAID: 'badge-paid'
    };
    return statusClasses[status] || 'badge-default';
  };

  return (
    <div className="payroll-page-container">
      {!payrollEnabled ? (
        <UpgradePrompt feature="Payroll" requiredPlan="GOLD" />
      ) : (<>
      <div className="page-header">
        <h1>Payroll Management</h1>
        <button className="btn btn-primary" onClick={handleProcessPayroll}>
          Process Payroll
        </button>
      </div>

      <div className="filters-section">
        <div className="pay-filter-row">
          <label className="pay-filter-label">Year</label>
          <select name="year" value={filters.year} onChange={handleFilterChange}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="pay-filter-row">
          <label className="pay-filter-label">Month</label>
          <select name="month" value={filters.month} onChange={handleFilterChange}>
            <option value="">All Months</option>
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div className="pay-filter-row">
          <label className="pay-filter-label">Employee</label>
          <select name="employeeId" value={filters.employeeId} onChange={handleFilterChange}>
            <option value="">All Employees</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.name} ({e.employeeCode})</option>
            ))}
          </select>
        </div>

        <button className="btn btn-secondary pay-clear-btn" onClick={handleClearFilters}>
          Clear Filters
        </button>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Loading payroll records...</div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Employee Code</th>
                  <th>Pay Period</th>
                  <th>Gross Salary</th>
                  <th>Deductions</th>
                  <th>Net Salary</th>
                  <th>Status</th>
                  <th>Payment Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payrollRecords.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">No payroll records found</td>
                  </tr>
                ) : (
                  payrollRecords.map(payroll => (
                    <tr key={payroll.id}>
                      <td data-label="Employee"><strong>{payroll.employeeName}</strong></td>
                      <td data-label="Code">{payroll.employeeCode}</td>
                      <td data-label="Pay Period">
                        {formatDate(payroll.payPeriodStart)} - {formatDate(payroll.payPeriodEnd)}
                      </td>
                      <td data-label="Gross Salary" className="amount">{formatCurrency(payroll.baseSalary)}</td>
                      <td data-label="Deductions" className="amount">{formatCurrency(payroll.deductions)}</td>
                      <td data-label="Net Salary" className="amount amount-highlight">{formatCurrency(payroll.netSalary)}</td>
                      <td data-label="Status">
                        <span className={`badge ${getStatusBadgeClass(payroll.status)}`}>
                          {payroll.status}
                        </span>
                      </td>
                      <td data-label="Payment Date">{formatDate(payroll.paymentDate)}</td>
                      <td data-label="Actions" className="actions">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleDownloadSalarySlip(payroll.id, payroll.employeeCode)}
                            title="Download Salary Slip"
                          >
                            📄 Download Slip
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 0}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {pagination.currentPage + 1} of {pagination.totalPages}
                  {' '}({pagination.totalElements} total)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= pagination.totalPages - 1}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showProcessModal && (
        <ProcessPayrollModal
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
      </>)}
    </div>
  );
}

export default PayrollPage;
